if (!customElements.get("video-carousel")) {
  class VideoCarousel extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        slideContainer: ".video-carousel-track",
        slideWrapper: ".video-carousel-slides",
        slides: ".video-carousel-slide",
        prevButton: ".video-nav-prev",
        nextButton: ".video-nav-next",
        thumbnails: ".video-thumbnail",
        dots: ".video-dot",
        videos: "video, iframe"
      };

      this.classes = {
        active: 'active',
        loading: 'video-loading'
      };

      this.currentSlide = 0;
      this.isTransitioning = false;

      // Get data attributes
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 10;
      this.showNavigation = this.dataset.showNavigation === "true";
      this.showThumbnails = this.dataset.showThumbnails === "true";
      this.videoAutoplay = this.dataset.videoAutoplay === "true";
      this.total = parseInt(this.dataset.total) || 0;

      this.init();
    }

    init() {
      this.setupEventListeners();
      this.initializeSlides();
      this.setupKeyboardNavigation();
      this.setupTouchNavigation();
      
      if (this.autoplay && this.total > 1) {
        this.startAutoplay();
      }
    }

    setupEventListeners() {
      // Navigation buttons
      const prevButton = this.querySelector(this.selectors.prevButton);
      const nextButton = this.querySelector(this.selectors.nextButton);
      
      if (prevButton) {
        prevButton.addEventListener('click', () => this.prevSlide());
      }
      
      if (nextButton) {
        nextButton.addEventListener('click', () => this.nextSlide());
      }

      // Thumbnail navigation
      const thumbnails = this.querySelectorAll(this.selectors.thumbnails);
      thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener('click', () => this.goToSlide(index));
      });

      // Dot navigation
      const dots = this.querySelectorAll(this.selectors.dots);
      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index));
      });

      // Pause autoplay on hover
      this.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.addEventListener('mouseleave', () => this.resumeAutoplay());

      // Video event listeners
      this.setupVideoEventListeners();
    }

    setupVideoEventListeners() {
      const videos = this.querySelectorAll('video');
      videos.forEach((video, index) => {
        // Pause video when slide changes
        video.addEventListener('loadstart', () => {
          const slide = video.closest(this.selectors.slides);
          slide.classList.add(this.classes.loading);
        });

        video.addEventListener('loadeddata', () => {
          const slide = video.closest(this.selectors.slides);
          slide.classList.remove(this.classes.loading);
        });

        // Auto-advance when video ends (if autoplay is enabled)
        video.addEventListener('ended', () => {
          if (this.autoplay && index === this.currentSlide) {
            setTimeout(() => this.nextSlide(), 1000);
          }
        });
      });
    }

    initializeSlides() {
      const slides = this.querySelectorAll(this.selectors.slides);
      
      // Hide all slides except first
      slides.forEach((slide, index) => {
        if (index === 0) {
          slide.classList.add(this.classes.active);
          this.playCurrentVideo();
        } else {
          slide.classList.remove(this.classes.active);
        }
      });

      this.updateNavigationStates();
    }

    nextSlide() {
      if (this.isTransitioning) return;
      
      const nextIndex = (this.currentSlide + 1) % this.total;
      this.goToSlide(nextIndex);
    }

    prevSlide() {
      if (this.isTransitioning) return;
      
      const prevIndex = this.currentSlide === 0 ? this.total - 1 : this.currentSlide - 1;
      this.goToSlide(prevIndex);
    }

    goToSlide(index) {
      if (this.isTransitioning || index === this.currentSlide || index >= this.total) return;

      this.isTransitioning = true;
      this.pauseAllVideos();

      const slides = this.querySelectorAll(this.selectors.slides);
      const slideWrapper = this.querySelector(this.selectors.slideWrapper);
      
      // Remove active class from current slide
      slides[this.currentSlide].classList.remove(this.classes.active);
      
      // Update current slide index
      this.currentSlide = index;
      
      // Add active class to new slide
      slides[this.currentSlide].classList.add(this.classes.active);
      
      // Animate slide transition
      if (slideWrapper) {
        const translateX = -this.currentSlide * 100;
        slideWrapper.style.transform = `translateX(${translateX}%)`;
      }

      // Update navigation states
      this.updateNavigationStates();
      
      // Play video in new slide if autoplay is enabled
      setTimeout(() => {
        this.playCurrentVideo();
        this.isTransitioning = false;
      }, 600); // Match CSS transition duration

      // Reset autoplay timer
      if (this.autoplay) {
        this.resetAutoplayTimer();
      }
    }

    updateNavigationStates() {
      // Update thumbnails
      const thumbnails = this.querySelectorAll(this.selectors.thumbnails);
      thumbnails.forEach((thumbnail, index) => {
        thumbnail.classList.toggle(this.classes.active, index === this.currentSlide);
      });

      // Update dots
      const dots = this.querySelectorAll(this.selectors.dots);
      dots.forEach((dot, index) => {
        dot.classList.toggle(this.classes.active, index === this.currentSlide);
      });

      // Update info sidebar
      const infoContents = this.querySelectorAll('.video-info-content');
      infoContents.forEach((content, index) => {
        if (index === this.currentSlide) {
          content.style.display = 'block';
          content.classList.add(this.classes.active);
        } else {
          content.style.display = 'none';
          content.classList.remove(this.classes.active);
        }
      });

      // Update navigation buttons visibility
      const prevButton = this.querySelector(this.selectors.prevButton);
      const nextButton = this.querySelector(this.selectors.nextButton);
      
      if (this.total <= 1) {
        if (prevButton) prevButton.style.display = 'none';
        if (nextButton) nextButton.style.display = 'none';
      } else {
        if (prevButton) prevButton.style.display = 'flex';
        if (nextButton) nextButton.style.display = 'flex';
      }
    }

    playCurrentVideo() {
      if (!this.videoAutoplay) return;

      const currentSlide = this.querySelectorAll(this.selectors.slides)[this.currentSlide];
      if (!currentSlide) return;

      const video = currentSlide.querySelector('video');
      if (video) {
        video.currentTime = 0;
        video.play().catch(e => {
          console.log('Video autoplay prevented:', e);
        });
      }
    }

    pauseAllVideos() {
      const videos = this.querySelectorAll('video');
      videos.forEach(video => {
        video.pause();
      });
    }

    setupKeyboardNavigation() {
      this.addEventListener('keydown', (e) => {
        switch(e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            this.prevSlide();
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.nextSlide();
            break;
          case ' ':
          case 'Spacebar':
            e.preventDefault();
            this.toggleCurrentVideo();
            break;
          case 'Escape':
            this.pauseAllVideos();
            break;
        }
      });

      // Make carousel focusable
      this.setAttribute('tabindex', '0');
    }

    setupTouchNavigation() {
      let startX = 0;
      let startY = 0;
      let isDragging = false;

      this.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = false;
      });

      this.addEventListener('touchmove', (e) => {
        if (!isDragging) {
          const deltaX = Math.abs(e.touches[0].clientX - startX);
          const deltaY = Math.abs(e.touches[0].clientY - startY);
          isDragging = deltaX > deltaY && deltaX > 10;
        }
        
        if (isDragging) {
          e.preventDefault();
        }
      });

      this.addEventListener('touchend', (e) => {
        if (isDragging) {
          const endX = e.changedTouches[0].clientX;
          const deltaX = endX - startX;
          
          if (Math.abs(deltaX) > 50) {
            if (deltaX > 0) {
              this.prevSlide();
            } else {
              this.nextSlide();
            }
          }
        }
      });
    }

    toggleCurrentVideo() {
      const currentSlide = this.querySelectorAll(this.selectors.slides)[this.currentSlide];
      if (!currentSlide) return;

      const video = currentSlide.querySelector('video');
      if (video) {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      }
    }

    startAutoplay() {
      this.autoplayInterval = setInterval(() => {
        this.nextSlide();
      }, this.autoplaySpeed * 1000);
    }

    pauseAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    }

    resumeAutoplay() {
      if (this.autoplay && !this.autoplayInterval) {
        this.startAutoplay();
      }
    }

    resetAutoplayTimer() {
      if (this.autoplay) {
        this.pauseAutoplay();
        this.startAutoplay();
      }
    }

    // Intersection Observer for performance
    setupIntersectionObserver() {
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // Carousel is visible, resume autoplay if needed
              this.resumeAutoplay();
            } else {
              // Carousel is not visible, pause everything
              this.pauseAutoplay();
              this.pauseAllVideos();
            }
          });
        }, { threshold: 0.5 });

        observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.pauseAutoplay();
      this.pauseAllVideos();
    }
  }

  customElements.define("video-carousel", VideoCarousel);
}